import { useEffect, useRef } from "react";
import { FiSearch, FiX } from "react-icons/fi";
import {
  MAX_ARCHIVE_SEARCH_QUERY_LENGTH,
  normalizeArchiveSearchQuery,
} from "@/features/archive/archiveSearch";
import {
  type ArchiveEvent,
  type ArchiveEventNotifier,
  isArchiveEvent,
} from "@/features/archive/mediators/archiveEvents";
import {
  type ArchiveSearchEvent,
  type ArchiveSearchState,
  createArchiveSearchMediator,
} from "@/features/archive/mediators/archiveSearchMediator";
import { SearchScopeSelect } from "@/features/search/components/SearchScopeSelect";
import type { SearchScope } from "@/features/search/searchScope";
import { cn } from "@/shared/lib";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  getHierarchyResourceSnapshot,
  useComponentNode,
  useHierarchyResource,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import { Input } from "@/shared/ui/form";

// biome-ignore lint/style/useComponentExportOnlyModules: 既存route/test向け互換export
export { MAX_ARCHIVE_SEARCH_QUERY_LENGTH, normalizeArchiveSearchQuery };

type ArchiveSearchBarProps = {
  readonly className?: string;
  readonly isFetching: boolean;
  readonly query: string;
  readonly scope: SearchScope;
  readonly onClear?: () => void;
  readonly onScopeChange?: (scope: SearchScope) => void;
  readonly onSubmit?: (query: string) => void;
};

export type ArchiveSearchBarViewProps = {
  readonly className?: string;
  readonly draft: string;
  readonly isSubmitDisabled: boolean;
  readonly scope: SearchScope;
  readonly onEvent: ArchiveEventNotifier;
};

function isArchiveSearchEvent(
  event: Parameters<typeof isArchiveEvent>[0],
): event is ArchiveSearchEvent {
  if (!isArchiveEvent(event)) return false;
  return (
    event.type === "archive/search-draft-changed" ||
    event.type === "archive/search-query-synchronized" ||
    event.type === "archive/search-submission-requested" ||
    event.type === "archive/search-clear-requested" ||
    event.type === "archive/search-scope-change-requested"
  );
}

/** URLを正本にする過去ログ専用検索のPresenter。 */
export const ArchiveSearchBar: React.FunctionComponent<
  ArchiveSearchBarProps
> = ({
  className,
  isFetching,
  query,
  scope,
  onClear,
  onScopeChange,
  onSubmit,
}: ArchiveSearchBarProps) => {
  const callbacks = useRef({ onClear, onScopeChange, onSubmit });
  callbacks.current = { onClear, onScopeChange, onSubmit };
  const mediator = useHierarchyResource((previous) =>
    createArchiveSearchMediator(
      query,
      previous?.isDisposed
        ? getHierarchyResourceSnapshot<ArchiveSearchState>(previous)
        : undefined,
    ),
  );
  const node = useComponentNode((event, context) => {
    if (!isArchiveEvent(event)) return "unhandled";

    if (
      event.type === "archive/search-committed" &&
      callbacks.current.onSubmit
    ) {
      callbacks.current.onSubmit(event.query);
      return "handled";
    }
    if (event.type === "archive/search-cleared" && callbacks.current.onClear) {
      callbacks.current.onClear();
      return "handled";
    }
    if (
      event.type === "archive/search-scope-changed" &&
      callbacks.current.onScopeChange
    ) {
      callbacks.current.onScopeChange(event.scope);
      return "handled";
    }
    if (!isArchiveSearchEvent(event)) return "unhandled";

    const result = mediator.dispatch(event);
    if (result.status !== "handled") return "unhandled";
    for (const effect of result.effects) {
      context.dispatch(effect.event);
    }
    return "handled";
  });
  const { draft } = useMediatorSnapshot(mediator);
  const notify: ArchiveEventNotifier = (
    event: ArchiveEvent,
  ): ReturnType<ArchiveEventNotifier> => node.dispatch(event).status;

  const lastSynchronizedQueryRef = useRef(query);
  useEffect(() => {
    if (lastSynchronizedQueryRef.current === query) return;
    lastSynchronizedQueryRef.current = query;
    mediator.dispatch({
      type: "archive/search-query-synchronized",
      query,
    });
  }, [mediator, query]);

  return (
    <ComponentScope node={node}>
      <ArchiveSearchBarView
        className={className}
        draft={draft}
        isSubmitDisabled={isFetching || (!draft.trim() && !query)}
        scope={scope}
        onEvent={notify}
      />
    </ComponentScope>
  );
};

/** 描画とtyped event通知だけを担う検索View。 */
export const ArchiveSearchBarView: React.FunctionComponent<
  ArchiveSearchBarViewProps
> = ({
  className,
  draft,
  isSubmitDisabled,
  scope,
  onEvent,
}: ArchiveSearchBarViewProps) => (
  <search
    aria-label="過去ログ検索"
    className={cn("w-full md:w-auto", className)}
  >
    <form
      className="catalog-search-form flex w-full items-center gap-2"
      onSubmit={(event: React.SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        onEvent({ type: "archive/search-submission-requested" });
      }}
    >
      <SearchScopeSelect
        ariaLabel="過去ログ検索の検索モード"
        scope={scope}
        onChange={(nextScope: SearchScope): void => {
          onEvent({
            type: "archive/search-scope-change-requested",
            scope: nextScope,
          });
        }}
      />
      <div
        className="catalog-search-field relative flex min-w-0 flex-1 items-center"
        data-has-query={draft ? "true" : "false"}
      >
        <Input
          type="search"
          value={draft}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
            onEvent({
              type: "archive/search-draft-changed",
              value: event.currentTarget.value,
            });
          }}
          placeholder="過去ログ検索"
          aria-label="過去ログ検索"
          maxLength={MAX_ARCHIVE_SEARCH_QUERY_LENGTH}
          className={`archive-search-input catalog-search-input min-h-11 min-w-0 flex-1 ${
            draft ? "pr-24" : "pr-12"
          }`}
        />
        <fieldset
          aria-label="過去ログ検索操作"
          className="absolute inset-y-0 right-0 m-0 flex min-w-0 items-center border-0 p-0"
        >
          {draft && (
            <button
              type="button"
              onClick={(): void => {
                onEvent({ type: "archive/search-clear-requested" });
              }}
              className="catalog-search-action flex size-11 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              aria-label="過去ログ検索をクリア"
            >
              <FiX size={18} aria-hidden="true" />
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitDisabled}
            className={cn(
              "catalog-search-action flex size-11 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50",
              draft && "catalog-search-action-divider",
            )}
            aria-label="過去ログを検索"
          >
            <FiSearch size={18} aria-hidden="true" />
          </button>
        </fieldset>
      </div>
    </form>
  </search>
);
